import clsx from 'clsx';
import { Download, Send, Trash2, Undo2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { addDays, addMonths, ENTRY_STATUSES, isWeekend, monthEnd, monthStart, weekStart, type EntryStatus } from '@pz/core';
import type { EntryView } from '@pz/server';
import { BilledCell, BilledDialog, EntryActions, EntryDialog, TaskBadges } from '../components/entry';
import { Button, Checkbox, EmptyState, ErrorBox, Field, Input, Loading, Notice, PageHeader, Pill, SortTh, StatusPill, td, th, useToast } from '../components/ui';
import { useTableSort } from '../lib/sort';
import { ScrollTable, Sentinel, useChunked } from '../components/virtual';
import { saveFile } from '../lib/download';
import { d, h, hh, wd } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { rpc } from '../lib/rpc';
import { useMe } from '../lib/me';
import { rowCheck, useRangeSelection } from '../lib/selection';

type Preset = 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'custom';

export function EntriesPage() {
  const { t } = useTranslation();
  const { me, can } = useMe();
  const toast = useToast();
  const [preset, setPreset] = useState<Preset>('thisMonth');
  const [from, setFrom] = useState(monthStart(me.today));
  const [to, setTo] = useState(monthEnd(me.today));
  const [statuses, setStatuses] = useState<EntryStatus[]>([]);
  const [trash, setTrash] = useState(false);
  const [edit, setEdit] = useState<EntryView | null>(null);
  const [billed, setBilled] = useState<EntryView | null>(null);
  const [exporting, setExporting] = useState(false);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'thisWeek') {
      const w = weekStart(me.today);
      setFrom(w);
      setTo(addDays(w, 6));
    } else if (p === 'thisMonth') {
      setFrom(monthStart(me.today));
      setTo(monthEnd(me.today));
    } else if (p === 'lastMonth') {
      const m = addMonths(monthStart(me.today), -1);
      setFrom(m);
      setTo(monthEnd(m));
    } else if (p === 'thisYear') {
      setFrom(`${me.today.slice(0, 4)}-01-01`);
      setTo(`${me.today.slice(0, 4)}-12-31`);
    }
  };

  // links from notifications: the entries on one task, or all entries on Forma tasks that were deleted (item 4)
  const [params, setParams] = useSearchParams();
  const linkTask = params.get('task') ?? '';
  const formaLost = params.get('forma') === 'lost';
  const linked = !!linkTask || formaLost;
  const team = formaLost && (can('time.manage') || me.leadProjectIds.length > 0);
  const linkFrom = addMonths(monthStart(me.today), -24);
  const q = useQ(
    'time.list',
    linked
      ? { from: linkFrom, to: addDays(me.today, 366), taskId: linkTask || undefined, formaLost: formaLost || undefined, allPersons: team || undefined }
      : { from, to, statuses: statuses.length ? statuses : undefined, onlyDeleted: trash || undefined },
  );
  const clearLink = () => setParams(new URLSearchParams(), { replace: true });
  // sorting by a column: personal, stored with the account (request 14)
  const sorting = useTableSort<EntryView>('entries', {
    date: (e) => `${e.workDate}|${e.createdAt}`,
    person: (e) => e.personName,
    task: (e) => `${e.taskRef} ${e.taskTitle}`,
    project: (e) => `${e.projectNumber} ${e.projectName}`,
    hours: (e) => e.minutes,
    billing: (e) => e.billingType,
    status: (e) => e.status,
  });
  const entries = useMemo(() => sorting.apply(q.data?.entries ?? []), [q.data, sorting.sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = useMemo(() => entries.reduce((s, e) => s + e.minutes, 0), [entries]);
  const order = useMemo(() => entries.map((e) => e.id), [entries]);
  const chunk = useChunked(entries, 100, `${from}|${to}|${statuses.join(',')}|${trash}|${sorting.sort?.key}|${sorting.sort?.dir}`);
  const sel = useRangeSelection(order);
  const picked = entries.filter((e) => sel.selected.has(e.id));
  const inv = { invalidate: ['time.', 'approvals.', 'kpi.', 'tasks.recent'] };
  const submitM = useM('time.submit', inv);
  const withdrawM = useM('time.withdraw', inv);
  const deleteM = useM('time.delete', inv);
  const restoreM = useM('time.restore', inv);
  const busy = submitM.isPending || withdrawM.isPending || deleteM.isPending;

  // several entries at once; each action can be undone from the message
  const bulkSubmit = async () => {
    const ids = picked.filter((e) => e.perm.submit).map((e) => e.id);
    if (!ids.length) return toast.info(t('entries.nothingFor'));
    try {
      const r = await submitM.mutateAsync({ ids });
      toast.undoable(t('entry.submittedDone', { count: r.submitted }), t('common.undo'), () => withdrawM.mutateAsync({ ids: r.ids }).catch(toast.error));
      sel.clear();
    } catch (e) {
      toast.error(e);
    }
  };
  const bulkWithdraw = async () => {
    const ids = picked.filter((e) => e.perm.withdraw).map((e) => e.id);
    if (!ids.length) return toast.info(t('entries.nothingFor'));
    try {
      const r = await withdrawM.mutateAsync({ ids });
      toast.ok(t('entry.withdrawnDone', { count: r.withdrawn }));
      sel.clear();
    } catch (e) {
      toast.error(e);
    }
  };
  const bulkDelete = async () => {
    // entries that need a reason (already approved) are deleted one by one with the reason dialog
    const ids = picked.filter((e) => e.perm.delete && !e.perm.deleteNeedsReason && !e.deletedAt).map((e) => e.id);
    if (!ids.length) return toast.info(t('entries.nothingFor'));
    const done: string[] = [];
    try {
      for (const id of ids) {
        await deleteM.mutateAsync({ id });
        done.push(id);
      }
      toast.undoable(t('entries.deletedMany', { count: done.length }), t('common.undo'), async () => {
        for (const id of done) await restoreM.mutateAsync({ id }).catch(toast.error);
      });
      sel.clear();
    } catch (e) {
      toast.error(e);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      saveFile(await rpc('time.exportMine', { from, to }));
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="pb-16">
      <PageHeader
        title={t('entries.title')}
        subtitle={t('entries.exportHint')}
        actions={
          <>
            <Button icon={<Trash2 className="size-4" />} variant={trash ? 'primary' : 'secondary'} onClick={() => setTrash((x) => !x)}>
              {t('entries.trash')}
            </Button>
            <Button icon={<Download className="size-4" />} loading={exporting} onClick={doExport}>
              {t('common.exportExcel')}
            </Button>
          </>
        }
      />
      {linked && (
        <Notice tone="warn" className="mb-3">
          <span className="flex flex-wrap items-center gap-2">
            {formaLost ? t('entries.formaLostFilter') : t('entries.taskFilter')}
            <Button size="sm" onClick={clearLink}>
              {t('entries.clearFilter')}
            </Button>
          </span>
        </Notice>
      )}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-line bg-sheet p-3">
        <div className="flex flex-wrap gap-1">
          {(['thisWeek', 'thisMonth', 'lastMonth', 'thisYear'] as const).map((p) => (
            <Button key={p} size="sm" variant={preset === p ? 'primary' : 'secondary'} onClick={() => applyPreset(p)}>
              {t(`entries.${p}`)}
            </Button>
          ))}
        </div>
        <Field label={t('common.from')} htmlFor="entries-from">
          <Input id="entries-from" type="date" value={from} onChange={(e) => (setPreset('custom'), setFrom(e.target.value))} className="w-40" />
        </Field>
        <Field label={t('common.to')} htmlFor="entries-to">
          <Input id="entries-to" type="date" value={to} onChange={(e) => (setPreset('custom'), setTo(e.target.value))} className="w-40" />
        </Field>
        <div className="flex flex-wrap gap-1">
          {ENTRY_STATUSES.map((s) => {
            const on = statuses.includes(s);
            return (
              <button
                key={s}
                onClick={() => setStatuses((x) => (on ? x.filter((y) => y !== s) : [...x, s]))}
                className={clsx('rounded-full border px-2 py-0.5 text-xs', on ? 'border-accent bg-accent-soft text-accent' : 'border-line text-graphite hover:text-ink')}
              >
                {t(`status.${s}`)}
              </button>
            );
          })}
        </div>
        <div className="num ml-auto text-sm">
          <span className="text-graphite">{t('entries.count', { count: entries.length })} · </span>
          <span className="font-semibold">{hh(total)}</span>
        </div>
      </div>

      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && entries.length === 0 && <EmptyState>{t('common.noData')}</EmptyState>}
      {q.data?.capped && <Notice tone="warn">{t('entries.capped', { count: entries.length })}</Notice>}
      {entries.length > 0 && (
        <ScrollTable label={t('entries.title')}>
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={clsx(th, 'w-8')}>
                  <Checkbox label={<span className="sr-only">{t('common.selectAll')}</span>} checked={sel.all} onChange={sel.toggleAll} />
                </th>
                <SortTh label={t('common.date')} sortKey="date" sort={sorting.sort} onSort={sorting.toggle} />
                {team && <SortTh label={t('common.person')} sortKey="person" sort={sorting.sort} onSort={sorting.toggle} />}
                <SortTh label={t('common.task')} sortKey="task" sort={sorting.sort} onSort={sorting.toggle} />
                <th className={th}>{t('common.description')}</th>
                <SortTh label={t('common.hours')} sortKey="hours" sort={sorting.sort} onSort={sorting.toggle} right />
                <SortTh label={t('entry.billingType')} sortKey="billing" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('common.status')} sortKey="status" sort={sorting.sort} onSort={sorting.toggle} />
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {chunk.visible.map((e) => (
                <tr key={e.id} className={clsx('border-b border-line/70', e.deletedAt && 'text-graphite', sel.selected.has(e.id) && 'bg-accent-soft/50')}>
                  <td className={td}>
                    <Checkbox label={<span className="sr-only">{t('common.select')}</span>} {...rowCheck(sel, e.id)} />
                  </td>
                  <td className={clsx(td, 'num whitespace-nowrap', isWeekend(e.workDate) && 'text-warn')} title={isWeekend(e.workDate) ? t('approvals.weekendHint') : undefined}>
                    {wd(e.workDate)} {d(e.workDate)}
                  </td>
                  {team && <td className={td}>{e.personName}</td>}
                  <td className={td}>
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="num font-semibold text-accent">{e.taskRef}</span>
                      <span>{e.taskTitle}</span>
                      <TaskBadges formaState={e.taskFormaState} internal={e.projectInternal} deleted={e.taskDeleted} />
                    </div>
                    <div className="text-xs text-graphite">
                      {e.projectNumber} {e.projectName}
                    </div>
                  </td>
                  <td className={clsx(td, 'max-w-md whitespace-pre-line text-graphite')}>
                    {e.description}
                    {e.deleteReason && <div className="text-xs">{e.deleteReason}</div>}
                  </td>
                  <td className={clsx(td, 'num text-right')}>
                    <span className="font-semibold">{h(e.minutes)}</span>
                    <span className="block">
                      <BilledCell entry={e} onEdit={setBilled} />
                    </span>
                  </td>
                  <td className={td}>
                    <Pill>{t(`billingType.${e.billingType}`)}</Pill>
                  </td>
                  <td className={td}>
                    <StatusPill status={e.status} />
                  </td>
                  <td className={clsx(td, 'text-right')}>
                    <EntryActions entry={e} onEdit={setEdit} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-sheet-2">
                <td className={clsx(td, 'font-semibold')} colSpan={4}>
                  {t('entries.sum')}
                </td>
                <td className={clsx(td, 'num text-right font-semibold')}>{h(total)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
          {chunk.hasMore && <Sentinel onMore={chunk.more} />}
        </ScrollTable>
      )}
      {chunk.hasMore && <p className="mt-2 text-xs text-graphite">{t('entries.showing', { shown: chunk.shown, total: chunk.total })}</p>}
      {sel.count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-sheet px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
            <span className="text-sm">{t('common.selected', { count: sel.count })}</span>
            <span className="num text-sm text-graphite">{hh(picked.reduce((s, e) => s + e.minutes, 0))}</span>
            <span className="text-xs text-graphite">{t('common.shiftHint')}</span>
            <div className="ml-auto flex flex-wrap gap-2">
              <Button icon={<Trash2 className="size-4" />} variant="danger" loading={deleteM.isPending} disabled={busy} onClick={bulkDelete}>
                {t('common.delete')}
              </Button>
              <Button icon={<Undo2 className="size-4" />} disabled={busy} loading={withdrawM.isPending} onClick={bulkWithdraw}>
                {t('entry.withdraw')}
              </Button>
              <Button variant="primary" icon={<Send className="size-4" />} disabled={busy} loading={submitM.isPending} onClick={bulkSubmit}>
                {t('entry.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
      <EntryDialog open={!!edit} entry={edit} onClose={() => setEdit(null)} />
      <BilledDialog entry={billed} onClose={() => setBilled(null)} />
    </div>
  );
}
